import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  Zoom,
} from "@mui/material";
import { EDITOR_ACCENT } from "../newsletter-editor/editorUiConstants";
import { formatDate } from "../../../utils/formatters";
import { contentPrimaryButtonSx } from "../content/contentEditorStyles";

const NewsletterPublishSummaryDialog = ({
  open,
  recipients,
  subject,
  htmlPreview,
  onClose,
  onConfirm,
  sending,
  scheduledAt,
}) => {
  const [selectedEmails, setSelectedEmails] = useState({});

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const initial = {};
    recipients.forEach((item) => {
      initial[item.email] = true;
    });
    setSelectedEmails(initial);
  }, [open, recipients]);

  const checkedEmails = useMemo(
    () => recipients.filter((item) => selectedEmails[item.email]).map((item) => item.email),
    [recipients, selectedEmails]
  );

  const allChecked = recipients.length > 0 && checkedEmails.length === recipients.length;
  const someChecked = checkedEmails.length > 0 && checkedEmails.length < recipients.length;

  const toggleAll = () => {
    if (allChecked) {
      setSelectedEmails({});
      return;
    }
    const next = {};
    recipients.forEach((item) => {
      next[item.email] = true;
    });
    setSelectedEmails(next);
  };

  const sendLabel = scheduledAt ? "Programmazione" : "Invio immediato";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth slots={{ transition: Zoom }}>
      <DialogTitle sx={{ textAlign: "center", color: EDITOR_ACCENT, fontWeight: 800 }}>
        Riepilogo invio
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "78vh" }}>
        <Stack spacing={1.5} sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
              Oggetto
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {subject?.trim() || "—"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
              {sendLabel}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {scheduledAt ? formatDate(scheduledAt) : "Invio subito dopo la conferma"}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", display: "block", mb: 1 }}>
            Anteprima email
          </Typography>
          <Box
            component="iframe"
            title="Anteprima template newsletter"
            sandbox=""
            srcDoc={htmlPreview?.trim() || "<p style=\"font-family:sans-serif;color:#64748b;padding:16px;\">Nessun contenuto HTML disponibile.</p>"}
            sx={{
              width: "100%",
              minHeight: 280,
              maxHeight: 360,
              border: "1px solid #e2e8f0",
              borderRadius: 1,
              bgcolor: "#fff",
            }}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Deseleziona i destinatari da escludere prima dell&apos;invio definitivo.
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={allChecked} indeterminate={someChecked} onChange={toggleAll} />}
          label={`Seleziona tutti (${checkedEmails.length}/${recipients.length})`}
        />
        <List dense sx={{ maxHeight: 240, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 1 }}>
          {recipients.map((item) => (
            <ListItem key={item.email} disablePadding>
              <ListItemButton onClick={() => setSelectedEmails((current) => ({ ...current, [item.email]: !current[item.email] }))}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox edge="start" checked={Boolean(selectedEmails[item.email])} tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText primary={item.email} secondary={item.roleLabel || item.role} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={sending}>
          Annulla
        </Button>
        <Button
          variant="contained"
          disabled={sending || checkedEmails.length === 0}
          sx={contentPrimaryButtonSx}
          onClick={() => onConfirm(checkedEmails)}
        >
          {sending ? "Invio in corso..." : "CONFERMA"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NewsletterPublishSummaryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string.isRequired,
      role: PropTypes.string,
      roleLabel: PropTypes.string,
    })
  ).isRequired,
  subject: PropTypes.string,
  htmlPreview: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  sending: PropTypes.bool,
  scheduledAt: PropTypes.string,
};

export default NewsletterPublishSummaryDialog;
